import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Hierarchical pickers for the admin panel.
 *
 * Sam thinks in names ("כיתה ט׳ · אלגברה · שיעור 1"), not ids. Any form that
 * needs a lesson/clip foreign key uses these Cascaders — never a raw
 * InputNumber<id>. The value stored is still the numeric id; only the display
 * is name-oriented.
 *
 * A single `ContentTreeProvider` at the top of each consuming screen fetches
 * the tree once and shares it with every picker / label resolver on that
 * screen — dozens of resolved rows don't each trigger their own fetch.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { Cascader } from 'antd';
import { getContentTree } from '../api';
import { useLoader } from './useLoader';
const ContentTreeContext = createContext({ tree: null, loading: false });
export function ContentTreeProvider({ children }) {
    const { data, loading } = useLoader(getContentTree, []);
    const value = useMemo(() => ({ tree: data, loading }), [data, loading]);
    return _jsx(ContentTreeContext.Provider, { value: value, children: children });
}
function useContentTree() {
    return useContext(ContentTreeContext);
}
function buildLessonTree(tree) {
    return tree.grades.map((g) => ({
        value: g.id,
        label: g.name,
        children: g.tracks.map((t) => ({
            value: t.id,
            label: t.name,
            children: t.lessons.map((l) => ({ value: l.id, label: l.title })),
            disabled: t.lessons.length === 0,
        })),
        disabled: g.tracks.every((t) => t.lessons.length === 0),
    }));
}
function buildClipTree(tree) {
    return tree.grades.map((g) => ({
        value: g.id,
        label: g.name,
        children: g.tracks.map((t) => ({
            value: t.id,
            label: t.name,
            children: t.lessons.map((l) => ({
                value: l.id,
                label: l.title,
                children: l.clips.map((c) => ({ value: c.id, label: c.title })),
                disabled: l.clips.length === 0,
            })),
            disabled: t.lessons.every((l) => l.clips.length === 0),
        })),
        disabled: g.tracks.every((t) => t.lessons.every((l) => l.clips.length === 0)),
    }));
}
/**
 * Locate the path (grade→track→lesson[→clip]) for a given leaf id inside the
 * built option tree. Depth = 3 for lesson picker, 4 for clip picker. Returns
 * the array of numeric values along the path, or null if the id isn't found.
 */
function findPath(options, leafId, depth, acc = []) {
    for (const opt of options) {
        const next = [...acc, opt.value];
        if (next.length === depth) {
            if (opt.value === leafId)
                return next;
            continue;
        }
        if (opt.children) {
            const hit = findPath(opt.children, leafId, depth, next);
            if (hit)
                return hit;
        }
    }
    return null;
}
/**
 * Lesson picker: 3-level cascade כיתה → מסלול → שיעור. Renders the leaf
 * lesson title in the trigger with grade + track as breadcrumbs.
 */
export function LessonCascader({ value, onChange, placeholder = 'בחר שיעור', disabled }) {
    const { tree, loading } = useContentTree();
    const options = useMemo(() => tree ? buildLessonTree(tree) : [], [tree]);
    const path = useMemo(() => (value != null && tree) ? findPath(options, value, 3) : null, [options, value, tree]);
    return (_jsx(Cascader, { options: options, value: path ?? undefined, onChange: (vals) => {
            const leaf = vals && vals.length === 3 ? Number(vals[2]) : null;
            onChange?.(leaf);
        }, placeholder: placeholder, showSearch: { filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }, displayRender: (labels) => labels.join(' · '), loading: loading, disabled: disabled, style: { width: '100%' }, changeOnSelect: false, expandTrigger: "hover" }));
}
/**
 * Clip picker: 4-level cascade כיתה → מסלול → שיעור → קליפ.
 */
export function ClipCascader({ value, onChange, placeholder = 'בחר קליפ', disabled }) {
    const { tree, loading } = useContentTree();
    const options = useMemo(() => tree ? buildClipTree(tree) : [], [tree]);
    const path = useMemo(() => (value != null && tree) ? findPath(options, value, 4) : null, [options, value, tree]);
    return (_jsx(Cascader, { options: options, value: path ?? undefined, onChange: (vals) => {
            const leaf = vals && vals.length === 4 ? Number(vals[3]) : null;
            onChange?.(leaf);
        }, placeholder: placeholder, showSearch: { filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }, displayRender: (labels) => labels.join(' · '), loading: loading, disabled: disabled, style: { width: '100%' }, changeOnSelect: false, expandTrigger: "hover" }));
}
/**
 * Resolve a lesson id to its "כיתה · מסלול · שיעור" label using the shared
 * content tree. Returns null while the tree is loading or if the id is
 * unknown. Rows/detail cards use this so raw ids never leak to the UI.
 */
export function useLessonLabel(lessonId) {
    const { tree } = useContentTree();
    if (lessonId == null || !tree)
        return null;
    for (const g of tree.grades)
        for (const t of g.tracks)
            for (const l of t.lessons) {
                if (l.id === lessonId)
                    return `${g.name} · ${t.name} · ${l.title}`;
            }
    return null;
}
export function useClipLabel(clipId) {
    const { tree } = useContentTree();
    if (clipId == null || !tree)
        return null;
    for (const g of tree.grades)
        for (const t of g.tracks)
            for (const l of t.lessons)
                for (const c of l.clips) {
                    if (c.id === clipId)
                        return `${g.name} · ${t.name} · ${l.title} · ${c.title}`;
                }
    return null;
}
